import { useState } from 'react';
import styles from './ContactForm.module.scss';
import { BloodTestForm, ECGForm, ImageScanForm } from './ServicesForm';
import { postRequest } from '../../services/api';
import { v4 as uuidv4 } from 'uuid';

const NordCustomerRequestForm = () => {
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		phoneNumber: '',
		email: '',
		dateOfBirth: '',
		gender: '',
		address: '',
		postcode: '',

		travel: null as boolean | null,
		accessibilityRequirements: '',
		preferredDatesForTesting: '',
		operativeCheckStatus: '',

		bloodTestRequired: null as boolean | null,
		badBloodExtractionExperience: null as boolean | null,
		badBloodExtractionExperienceExplanation: '',
		bloodTestTypeRequired: '',
		firstMostSuitableLocation: '',
		firstOtherLocation: '',
		secondMostSuitableLocation: '',

		imagingScanRequired: null as boolean | null,
		medicalDetailForImageScan: '',
		explainedBodyPart: '',
		imageTestTypeRequested: '',
		otherImagingTestRequested: '',
		firstMostPreferredImageTestLocation: '',
		secondMostPreferredImageTestLocation: '',
		heartFitterInstalledMRI: '',
		shuntOrClipInstalledMRI: '',
		implantOrNeurotransmitterMRI: '',
		renalImpairmentMRI: '',
		proceduresSixMonthsMRI: '',
		metallicForeignBodiesEyeMRI: '',
		breastFeedingMRI: '',
		pregnantMRI: '',
		paidProfessionalSport: '',
		complexRequestNordClinic: '',
		lastImageScanDate: '',

		restingECGRequested: null as boolean | null,
		selectedECGLocation: '',
		otherECGLocation: '',
		consentConfirmationECG: null as boolean | null,

		anotherServiceRequired: null as boolean | null,
		requestedServiceDetails: '',

		preferredContactMethod: [] as string[] | null,
		shareResultsNordClinic: null as boolean | null,
		consentImageReportSirkka: null as boolean | null,
		consentMedicalResearch: null as boolean | null,
		isRegisteredSirkkaWebsite: null as boolean | null,
	});
	const [formSubmitSuccessStatus, setFormSubmitSuccessStatus] = useState<boolean | null>(null);
	const [page, setPage] = useState(1);

	const TOTAL_PAGES = 3;

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			const mappedFormData = mapNordFormData(formData);
			const response = await postRequest('/staff/crm/ticket/create', mappedFormData);

			console.log(response);

			if (response.status === 'success') {
				setFormSubmitSuccessStatus(true);
			} else {
				console.error('Submission failed with status:', response.status);
				setFormSubmitSuccessStatus(false);
			}
		} catch (error: any) {
			console.log(error);
			setFormSubmitSuccessStatus(false);
		}
	};

	const mapNordFormData = (form: any) => {
		const data = {
			ticket_uuid: uuidv4(),
			first_name: form.firstName,
			last_name: form.lastName,
			phone_number: form.phoneNumber,
			email: form.email,
			date_of_birth: form.dateOfBirth,
			gender: form.gender,
			// add postcode to address
			address: `${form.address}, ${form.postcode}`,

			accessibility_requirements: form.accessibilityRequirements,
			can_travel: form.travel,

			preferred_appt_time: form.preferredDatesForTesting,
			operative_check_status: form.operativeCheckStatus,

			blood_test_required: form.bloodTestRequired,
			blood_draw_bad_experience: form.badBloodExtractionExperience,
			blood_draw_experience_details: form.badBloodExtractionExperienceExplanation,
			service_type: form.bloodTestTypeRequired, // blood test type
			blood_test_location_primary: form.firstOtherLocation
				? form.firstOtherLocation
				: form.firstMostSuitableLocation,
			blood_test_location_secondary: form.secondMostSuitableLocation,

			imaging_required: form.imagingScanRequired,
			medical_details_for_image_scan: form.medicalDetailForImageScan,
			imaging_body_part_details: form.explainedBodyPart,
			image_test_type_required: form.otherImagingTestRequested
				? form.otherImagingTestRequested
				: form.imageTestTypeRequested,
			imaging_location_preferred: form.firstMostPreferredImageTestLocation,
			imaging_location_preferred_secondary: form.secondMostPreferredImageTestLocation,
			is_heart_fitter_installed: form.heartFitterInstalledMRI,
			is_shunt_or_clip_installed: form.shuntOrClipInstalledMRI,
			is_implant_or_neurotransmitter_installed: form.implantOrNeurotransmitterMRI,
			has_renal_impairment: form.renalImpairmentMRI,
			had_any_surgery_last_six_months: form.proceduresSixMonthsMRI,
			had_metallic_bodies_in_eye: form.metallicForeignBodiesEyeMRI,
			is_breastfeeding: form.breastFeedingMRI,
			is_pregnant: form.pregnantMRI,
			is_involved_in_professional_sports: form.paidProfessionalSport,
			complex_request_details: form.complexRequestNordClinic,
			previous_image_scan_date: form.lastImageScanDate,

			ecg_required: form.restingECGRequested,
			ecg_location_preferred: form.otherECGLocation
				? form.otherECGLocation
				: form.selectedECGLocation,
			share_ecg_result: form.consentConfirmationECG,

			other_service_required: form.anotherServiceRequired,
			other_service_details: form.requestedServiceDetails,

			contact_method_preferred: Array.isArray(form.preferredContactMethod)
				? form.preferredContactMethod.join(', ')
				: form.preferredContactMethod,
			share_results_with_nord: form.shareResultsNordClinic,
			share_imaging_report: form.consentImageReportSirkka,
			anonymised_data_research_consent: form.consentMedicalResearch,
			sirkka_registered: form.isRegisteredSirkkaWebsite,
		};

		return cleanData(data);
	};

	const cleanData = (data: Record<string, any>): Record<string, any> => {
		const cleanedData: Record<string, any> = {};

		Object.keys(data).forEach((key) => {
			const value = data[key];

			if (value !== '' && value !== null && value !== undefined) {
				cleanedData[key] = value;
			}
		});

		return cleanedData;
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleMultipleCheckbox = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value, type } = e.target;

		if (type === 'checkbox' && name === 'preferredContactMethod') {
			const { checked } = e.target as HTMLInputElement;

			setFormData((prevFormData) => {
				const currentValues = prevFormData.preferredContactMethod || [];
				if (checked) {
					return {
						...prevFormData,
						preferredContactMethod: [...currentValues, value],
					};
				} else {
					return {
						...prevFormData,
						preferredContactMethod: currentValues.filter((v) => v !== value),
					};
				}
			});
		}
	};

	const handleBooleanToggle = (field: keyof typeof formData, value: boolean) => {
		setFormData((prevState) => ({
			...prevState,
			[field]: value,
		}));
	};

	const handleNextPage = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		if (page < TOTAL_PAGES) {
			console.log('Switching to page', page + 1);
			setPage((prev) => prev + 1);
		}
	};

	const handlePrevPage = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();
		if (page > 1) {
			console.log('Switching to page', page - 1);
			setPage((prev) => prev - 1);
		}
	};

	if (formSubmitSuccessStatus) {
		return (
			<>
				<div className={styles['contact-form-container']}>
					<h1 className={styles['form-title']}>Thank you for your submission!</h1>
					<p className="text-center">
						We've received your enquiry and will get back to you shortly.
					</p>
					<p className="text-center">
						If you have any further questions, feel free to email us at{' '}
						<a href="mailto:customers@sirkkaltd.com">
							<u>customers@sirkkaltd.com</u>
						</a>
						.
					</p>
				</div>
			</>
		);
	}

	return (
		<>
			<div className={styles['contact-form-container']}>
				<h1 className={styles['form-title']}>Nord Clinic customer enquiry form</h1>
				<p className="text-center">
					Please complete this form with as much detail as you can. If you have any issues, concerns
					or feedback, please contact us via email at{' '}
					<a href="mailto:customers@sirkkaltd.com">
						<u>customers@sirkkaltd.com</u>
					</a>
					.
				</p>

				<form onSubmit={handleSubmit} className={styles['contact-form']}>
					{page === 1 && (
						<Page1
							formData={formData}
							handleChange={handleChange}
							handleBooleanToggle={handleBooleanToggle}
						/>
					)}
					{page === 2 && (
						<Page2
							formData={formData}
							handleChange={handleChange}
							handleBooleanToggle={handleBooleanToggle}
							setFormData={setFormData}
						/>
					)}
					{page === 3 && (
						<Page3
							formData={formData}
							handleMultipleCheckbox={handleMultipleCheckbox}
							handleBooleanToggle={handleBooleanToggle}
						/>
					)}

					<div className={styles['flex-container']}>
						<button
							className="primary_btn"
							disabled={page === 1}
							onClick={handlePrevPage}
							type="button"
						>
							Back
						</button>

						{page < TOTAL_PAGES ? (
							<button className="primary_btn" type="button" onClick={handleNextPage}>
								Next
							</button>
						) : (
							<button className="primary_btn" type="submit">
								Submit
							</button>
						)}
					</div>
				</form>
			</div>
		</>
	);
};

const Page1 = ({ formData, handleChange, handleBooleanToggle }: any) => {
	return (
		<>
			<div className={styles['form-group']}>
				<label htmlFor="firstName">First name</label>
				<input
					type="text"
					id="firstName"
					name="firstName"
					value={formData.firstName}
					onChange={handleChange}
					required
					className={styles['form-select']}
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="lastName">Last name</label>
				<input
					type="text"
					id="lastName"
					name="lastName"
					value={formData.lastName}
					onChange={handleChange}
					required
					className={styles['form-select']}
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="phoneNumber">Telephone number</label>
				<input
					type="text"
					id="phoneNumber"
					name="phoneNumber"
					value={formData.phoneNumber}
					onChange={handleChange}
					required
					className={styles['form-select']}
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="email">Email address</label>
				<input
					type="text"
					id="email"
					name="email"
					value={formData.email}
					onChange={handleChange}
					required
					className={styles['form-select']}
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="dateOfBirth">Date of Birth</label>
				<input
					type="date"
					id="dateOfBirth"
					name="dateOfBirth"
					value={formData.dateOfBirth}
					onChange={handleChange}
					required
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="gender">Gender</label>
				<input
					type="text"
					id="gender"
					name="gender"
					value={formData.gender}
					onChange={handleChange}
					required
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="address">Address</label>
				<input
					type="text"
					id="address"
					name="address"
					value={formData.address}
					onChange={handleChange}
					required
					className={styles['form-select']}
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="address">Postcode</label>
				<input
					type="text"
					id="postcode"
					name="postcode"
					value={formData.postcode}
					onChange={handleChange}
					required
					className={styles['form-select']}
				/>
			</div>

			<label>Can you travel?</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.travel === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('travel', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.travel === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('travel', false)}
				>
					No
				</button>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="accessibilityRequirements">
					Do you have any accessibility requirements? If yes, please elaborate below.
				</label>
				<input
					type="text"
					id="accessibilityRequirements"
					name="accessibilityRequirements"
					value={formData.accessibilityRequirements}
					onChange={handleChange}
					required
				/>
			</div>

			<div className={styles['form-group']}>
				<label htmlFor="preferredDatesForTesting">What are your preferred dates for testing?</label>
				<small>
					{' '}
					Please provide at least <u>three options</u> for dates, as well as preference for{' '}
					<u>morning or afternoon</u> appointments
				</small>
				<input
					type="text"
					id="preferredDatesForTesting"
					name="preferredDatesForTesting"
					value={formData.preferredDatesForTesting}
					onChange={handleChange}
					required
				/>
			</div>

			<label htmlFor="operativeCheckStatus">Is this pre-operative or post-operative check?</label>
			<div className={styles['checkbox-group']}>
				<label>
					<input
						type="checkbox"
						name="operativeCheckStatus"
						value="Pre-operative"
						checked={formData.operativeCheckStatus.includes('Pre-operative')}
						onChange={handleChange}
					/>
					Pre-operative
				</label>
				<label>
					<input
						type="checkbox"
						name="operativeCheckStatus"
						value="Post-operative"
						checked={formData.operativeCheckStatus.includes('Post-operative')}
						onChange={handleChange}
					/>
					Post-operative
				</label>
				<label>
					<input
						type="checkbox"
						name="operativeCheckStatus"
						value="General check"
						checked={formData.operativeCheckStatus.includes('General check')}
						onChange={handleChange}
					/>
					General check
				</label>
			</div>
		</>
	);
};

const Page2 = ({ formData, handleChange, handleBooleanToggle, setFormData }: any) => {
	return (
		<>
			<h2 className={styles['section-title']}>Test Specifics</h2>
			<p>
				The following section must be completed in full. You will be informed as to which locations
				are appropriate, and the (rough) cost of these.
			</p>
			<h2 className={styles['subsection-title']}>Blood Tests</h2>
			<label>Do you need a blood test performed?</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.bloodTestRequired === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('bloodTestRequired', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.bloodTestRequired === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('bloodTestRequired', false)}
				>
					No
				</button>
			</div>

			{formData.bloodTestRequired && (
				<BloodTestForm
					formData={formData}
					handleChange={handleChange}
					handleBooleanToggle={handleBooleanToggle}
				/>
			)}

			<h2 className={styles['subsection-title']}>Imaging Scans </h2>
			<label>Do you need imaging scans (e.g. X-Ray, MRI, CT, ultrasound)?</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.imagingScanRequired === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('imagingScanRequired', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.imagingScanRequired === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('imagingScanRequired', false)}
				>
					No
				</button>
			</div>

			{formData.imagingScanRequired && (
				<ImageScanForm formData={formData} handleChange={handleChange} setFormData={setFormData} />
			)}

			<h2 className={styles['subsection-title']}>Resting ECG</h2>
			<label>Do you need a resting ECG performed?</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.restingECGRequested === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('restingECGRequested', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.restingECGRequested === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('restingECGRequested', false)}
				>
					No
				</button>
			</div>

			{formData.restingECGRequested && (
				<ECGForm
					formData={formData}
					handleChange={handleChange}
					handleBooleanToggle={handleBooleanToggle}
				/>
			)}

			<h2 className={styles['subsection-title']}>Other service</h2>
			<label>Do you need another service (such as a virtual consultation)?</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.anotherServiceRequired === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('anotherServiceRequired', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.anotherServiceRequired === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('anotherServiceRequired', false)}
				>
					No
				</button>
			</div>

			{formData.anotherServiceRequired && (
				<>
					<label htmlFor="requestedServiceDetails">Please provide details for this below.</label>
					<input
						type="text"
						id="requestedServiceDetails"
						name="requestedServiceDetails"
						value={formData.requestedServiceDetails}
						onChange={handleChange}
						required
					/>
				</>
			)}
		</>
	);
};

const Page3 = ({ formData, handleMultipleCheckbox, handleBooleanToggle }: any) => {
	return (
		<>
			<h2 className={styles['section-title']}>Final Section</h2>

			<label htmlFor="preferredContactMethod">
				Please advise what your preferred method of contact for this enquiry is.
			</label>
			<div className={styles['checkbox-group']}>
				<label>
					<input
						type="checkbox"
						name="preferredContactMethod"
						value="Email"
						checked={formData.preferredContactMethod.includes('Email')}
						onChange={handleMultipleCheckbox}
					/>
					Email
				</label>
				<label>
					<input
						type="checkbox"
						name="preferredContactMethod"
						value="Phone"
						checked={formData.preferredContactMethod.includes('Phone')}
						onChange={handleMultipleCheckbox}
					/>
					Phone
				</label>
			</div>

			<label>Would you like us to share your results with Nord Clinic?</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.shareResultsNordClinic === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('shareResultsNordClinic', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.shareResultsNordClinic === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('shareResultsNordClinic', false)}
				>
					No
				</button>
			</div>

			<label>
				I hereby consent for my imaging report and images to be sent to Sirkka Health to be shared
				with my referring clinic/clinicians.
			</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.consentImageReportSirkka === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('consentImageReportSirkka', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.consentImageReportSirkka === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('consentImageReportSirkka', false)}
				>
					No
				</button>
			</div>

			<label>
				I hereby consent for completely anonymised medical data related to these tests to be used
				for medical research purposes.
			</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.consentMedicalResearch === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('consentMedicalResearch', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.consentMedicalResearch === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('consentMedicalResearch', false)}
				>
					No
				</button>
			</div>

			<label>
				Have you registered on Sirkka Health's website (
				<a href="https://sirkkahealth.com/login">
					<u>https://sirkkahealth.com/login</u>
				</a>
				)?{' '}
				<b>
					<u>We will need you to register in order to process your appointments.</u>
				</b>{' '}
				Once you validate your ID (UK passport/driving license), you can access test/scan results in
				your portal.{' '}
			</label>
			<div className={styles['toggle-buttons']}>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.isRegisteredSirkkaWebsite === true ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('isRegisteredSirkkaWebsite', true)}
				>
					Yes
				</button>
				<button
					type="button"
					className={`${styles['toggle-button']} ${
						formData.isRegisteredSirkkaWebsite === false ? styles['active'] : ''
					}`}
					onClick={() => handleBooleanToggle('isRegisteredSirkkaWebsite', false)}
				>
					No
				</button>
			</div>
		</>
	);
};

export default NordCustomerRequestForm;
